import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProducts, createProduct, updateProduct, deleteProduct } from '../services/api';

function Admin() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    product_name: '',
    price: '',
    you_pay: '',
    status: 'active'
  });

  useEffect(() => {
    const apiKey = localStorage.getItem('api_key');
    if (!apiKey) {
      navigate('/signin');
      return;
    }
    fetchProducts();
  }, [navigate]);

  const fetchProducts = async () => {
    try {
      const data = await getProducts();
      if (data.success) {
        setProducts(data.products);
      } else {
        console.error('Failed to fetch products:', data.error);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        const response = await updateProduct(editingProduct.id, formData);
        if (response.success) {
          setProducts(products.map(p => p.id === editingProduct.id ? { ...p, ...formData } : p));
          setEditingProduct(null);
          await fetchProducts(); // Refresh to get latest data
        }
      } else {
        const response = await createProduct(formData);
        if (response.success) {
          await fetchProducts(); // Refresh to get latest data
        }
      }
      setFormData({ product_name: '', price: '', status: 'active' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      product_name: product.product_name,
      price: product.price,
      you_pay: product.you_pay,
      status: product.status
    });
    setShowAddForm(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        const response = await deleteProduct(productId);
        if (response.success) {
          setProducts(products.filter(p => p.id !== productId));
        }
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleStatusToggle = async (productId, newStatus) => {
    try {
      const product = products.find(p => p.id === productId);
      const updatedData = { ...product, status: newStatus };
      const response = await updateProduct(productId, updatedData);
      if (response.success) {
        setProducts(products.map(p => p.id === productId ? { ...p, status: newStatus } : p));
      }
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleCancel = () => {
    setEditingProduct(null);
    setFormData({ product_name: '', price: '', status: 'active' });
    setShowAddForm(false);
  };

  if (loading) {
    return (
      <div className="container mt-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading products...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h1 className="display-5 fw-light text-muted">Product Management</h1>
            <div className="d-flex gap-2">
              <button
                onClick={fetchProducts}
                className="btn btn-outline-secondary"
              >
                Refresh
              </button>
              <button
                onClick={() => setShowAddForm(true)}
                className="btn btn-primary"
              >
                Add Product
              </button>
            </div>
          </div>

          {showAddForm && (
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-body p-4">
                <h5 className="card-title mb-4">
                  {editingProduct ? 'Edit Product' : 'Add New Product'}
                </h5>
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Product Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.product_name}
                      onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Original Price ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      name="price"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="form-control"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">You Pay Price ($)</label>
                    <input
                      type="number"
                      step="0.01"
                      name="you_pay"
                      value={formData.you_pay}
                      onChange={(e) => setFormData({ ...formData, you_pay: e.target.value })}
                      className="form-control"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                  <div className="d-flex gap-2">
                    <button type="submit" className="btn btn-primary">
                      {editingProduct ? 'Update' : 'Add'} Product
                    </button>
                    <button type="button" onClick={handleCancel} className="btn btn-secondary">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="card border-0 shadow-sm">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th colSpan="6" className="text-center">Product Management</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map((product) => (
                      <tr key={product.id}>
                        <td className="text-center">{product.id}</td>
                        <td>{product.product_name}</td>
                        <td className="text-end">${parseFloat(product.price).toFixed(2)}</td>
                        <td className="text-end">${parseFloat(product.you_pay).toFixed(2)}</td>
                        <td className="text-center">
                          <span className={`badge ${product.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                            {product.status}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="btn-group" role="group">
                            <button
                              onClick={() => handleEdit(product)}
                              className="btn btn-sm btn-outline-primary me-2"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleStatusToggle(product.id, product.status === 'active' ? 'inactive' : 'active')}
                              className={`btn btn-sm ${product.status === 'active' ? 'btn-outline-warning' : 'btn-outline-success'}`}
                            >
                              {product.status === 'active' ? 'Deactivate' : 'Activate'}
                            </button>
                            <button
                              onClick={() => handleDelete(product.id)}
                              className="btn btn-sm btn-outline-danger"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {products.length === 0 && (
                <div className="text-center py-4">
                  <p className="text-muted">No products found. Add your first product!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;
